// The maintainer-gated `/analytics` dashboard (analytics+postgres Phase 6).
//
// This is the user-visible payoff: KPI tiles, a daily usage line, a per-project
// breakdown, and a per-user table behind an explicit, audited reveal. It renders
// only what the content-free contract carries (ids, counts, money, timestamps) —
// there is deliberately no client-side fetch that pulls titles/paths/MR URLs.
//
// Access is server-owned: a non-viewer gets a 403 and we render the denied state
// off that, not a client-side role check (no role lives in the cookie). The cost
// metric is now TOTAL spend across every metered surface (coding/pm/handoff/
// workflow, analytics+postgres Phase 9), with a per-surface split under the KPI;
// it is derived from token counts (best-effort), never an invoice.

import { useCallback, useMemo, useState, type JSX } from "react";
import { Link } from "@tanstack/react-router";

import type {
  AnalyticsNamedSessionRow,
  AnalyticsNamedUserRow,
  AnalyticsProjectRow,
  CostBySurface,
  SuppressibleCount,
} from "@loom/shared/analytics";

import { useFormatDate, useFormatNumber } from "../i18n/format.js";
import { useLocale, useT, type Translator } from "../i18n/index.js";

import { CostSplitBar, LineChart, ModeBar, type CostSplitSegment, type LinePoint } from "./charts.js";
import {
  actionKindLabel,
  outcomeLabel,
  personLabel,
  projectLabel,
  sessionLabel,
} from "./labels.js";
import { modelSourceLabelKey } from "./model-source.js";
import {
  useAnalyticsOverview,
  useNamedUsage,
  useSessionUsage,
  type AnalyticsProjection,
  type NamedStatus,
  type SessionTimelineState,
} from "./useAnalytics.js";
import { formatUsdDisplay } from "./usd.js";

interface AnalyticsPageProps {
  readonly onBack: () => void;
}

// USD display: the contract's 6-dp string projected for humans — 2-dp currency,
// widening to 4 fraction digits below one cent so real sub-cent model spend
// doesn't render as "$0.00" (see ./usd.ts).
const useUsd = (): ((value: string) => string) => {
  const { locale } = useLocale();
  return useCallback(
    (value: string) => formatUsdDisplay(value, locale),
    [locale],
  );
};

/**
 * "These numbers are a snapshot, and here is how old it can be."
 *
 * Every number on this page comes out of the `analytics.*` star schema, filled
 * by the `analytics-sync` cron — never a live read. Two refetches inside one
 * cron window therefore return byte-identical bodies BY CONSTRUCTION, and
 * anything a session did since the last run is structurally invisible. That is
 * working as designed; what was missing is the page saying so. Live, an
 * operator refetched the session drill-down three times in 18 minutes while
 * watching a running session, saw the same body each time, and read a
 * documented batch lag as a frozen ledger.
 *
 * `asOf` is the newest fact instant IN the projection — a lower bound on the
 * last sync, not the sync time: an idle fleet has an old watermark too. The
 * copy says "covers activity up to", never "last updated".
 *
 * Renders nothing at all against a server that does not send the block (it is
 * `.nullish()` for rolling deploys).
 */
/** Module-level so the `useFormatDate` callback identity is stable. */
const PROJECTION_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  dateStyle: "medium",
  timeStyle: "short",
};

const ProjectionNote = ({
  info,
}: {
  info: AnalyticsProjection | null | undefined;
}): JSX.Element | null => {
  const t = useT();
  const fmtDate = useFormatDate(PROJECTION_DATE_FORMAT);
  if (!info) return null;
  return (
    <p className="analytics-muted analytics-projection-note">
      {info.asOf === null
        ? t("analytics.projection.empty", { minutes: String(info.refreshMinutes) })
        : t("analytics.projection.note", {
            minutes: String(info.refreshMinutes),
            asOf: fmtDate(info.asOf),
          })}
    </p>
  );
};

const Cell = ({ count, band }: { count: SuppressibleCount; band: string }): JSX.Element => (
  <span className={count.suppressed ? "analytics-cell-suppressed" : undefined}>
    {count.suppressed ? band : count.value}
  </span>
);

// The four metered surfaces, in display order, mapped to their i18n label key.
// `costBySurface` is exhaustive (Phase 9), so this drives both the split bar and
// its aria/legend without a hand-maintained second list.
const COST_SURFACES: ReadonlyArray<{
  readonly key: keyof CostBySurface;
  readonly labelKey: Parameters<Translator>[0];
}> = [
  { key: "coding", labelKey: "analytics.cost.surface.coding" },
  { key: "pm", labelKey: "analytics.cost.surface.pm" },
  { key: "handoff", labelKey: "analytics.cost.surface.handoff" },
  { key: "workflow", labelKey: "analytics.cost.surface.workflow" },
];

const costSegments = (
  split: CostBySurface,
  t: Translator,
  usd: (value: string) => string,
): ReadonlyArray<CostSplitSegment> =>
  COST_SURFACES.map(({ key, labelKey }) => ({
    surface: key,
    label: t(labelKey),
    amount: split[key],
    display: usd(split[key]),
  }));

export const AnalyticsPage = ({ onBack }: AnalyticsPageProps): JSX.Element => {
  const t = useT();
  const num = useFormatNumber();
  const usd = useUsd();
  const fmtDate = useFormatDate({ dateStyle: "medium" });

  const overview = useAnalyticsOverview();
  const named = useNamedUsage();
  const sessions = useSessionUsage();
  // Independent audited reveal for the compact "recent sessions" widget — its
  // own hook so its reveal toggle is separate from the full drill-down panel.
  const recent = useSessionUsage();

  const linePoints: ReadonlyArray<LinePoint> = useMemo(
    () =>
      (overview.data?.daily ?? []).map((d) => ({
        label: d.day,
        value: d.sessionCount.value,
      })),
    [overview.data],
  );

  // --- States ----------------------------------------------------------------
  const shell = (body: JSX.Element): JSX.Element => (
    <div className="loom-dashboard analytics-page">
      <header className="analytics-head">
        <button className="analytics-back" onClick={onBack} type="button">
          ← {t("analytics.back")}
        </button>
        <div>
          <h1 className="analytics-title">{t("analytics.title")}</h1>
          <p className="analytics-subtitle">{t("analytics.subtitle")}</p>
        </div>
      </header>
      {body}
    </div>
  );

  if (overview.forbidden) {
    return shell(
      <div className="analytics-state analytics-denied">
        <h2>{t("analytics.denied.title")}</h2>
        <p>{t("analytics.denied.body")}</p>
      </div>,
    );
  }
  if (overview.error) {
    return shell(
      <div className="analytics-state analytics-state-error">
        <h2>{t("analytics.error.title")}</h2>
        <p>{overview.error}</p>
      </div>,
    );
  }
  if (overview.loading || !overview.data) {
    return shell(<div className="analytics-state">{t("analytics.loading")}</div>);
  }

  const { overview: agg, projects, models, billing, daily, keys } = overview.data;
  // Server-sorted cost-descending; re-sorted defensively so the table order
  // never depends on transport. `models` is absent against a pre-Phase-9
  // server during a rolling deploy.
  const modelRows = [...(models ?? [])].sort(
    (a, b) => Number(b.costUsd) - Number(a.costUsd),
  );
  // Spend by key comes only to an org-wide viewer; without it, neither the
  // section nor the project table's own-key column is drawn.
  const ownKeyCost = new Map<number, string>();
  for (const k of keys ?? []) {
    if (k.ownerProjectId !== null) ownKeyCost.set(k.ownerProjectId, k.costUsd);
  }

  // As-billed vs metered over the SAME calendar days: the billing block covers
  // the trailing 7 UTC days; the metered comparand is the daily series summed
  // over the billed window (bounded by the billed days actually present).
  const billedDays = billing?.days ?? [];
  const billedWindow =
    billedDays.length > 0
      ? { from: billedDays[0]!.day, to: billedDays[billedDays.length - 1]!.day }
      : null;
  const meteredInWindow = billedWindow
    ? daily
        .filter((d) => d.day >= billedWindow.from && d.day <= billedWindow.to)
        .reduce((sum, d) => sum + Number(d.totalCostUsd), 0)
    : 0;
  const billedTotal = billing ? Number(billing.totalBilledUsd) : 0;
  const nonTokenBilled = billing
    ? Number(billing.totalAllCostTypesUsd) - billedTotal
    : 0;
  const billingDeltaPct =
    billedTotal > 0 ? ((meteredInWindow - billedTotal) / billedTotal) * 100 : null;

  // --- Body ------------------------------------------------------------------
  return shell(
    <>
      {/* Every tile, chart and table below is a projection read — say so once,
          at the top, where the numbers start. */}
      <ProjectionNote info={overview.data.projection} />
      {/* KPI tiles — only the fields the content-free contract carries. */}
      <section className="analytics-kpis">
        <Kpi label={t("analytics.kpi.users")} value={num(agg.userCount)} />
        <Kpi label={t("analytics.kpi.sessions")} value={num(agg.sessionCount)} />
        <Kpi label={t("analytics.kpi.projects")} value={num(agg.projectCount)} />
        <div className="analytics-kpi">
          <span className="analytics-kpi-label">{t("analytics.kpi.modeMix")}</span>
          <ModeBar
            pm={agg.sessionsUsingPm}
            coding={agg.sessionsUsingCoding}
            pmLabel={t("analytics.kpi.modeMix.pm")}
            codingLabel={t("analytics.kpi.modeMix.coding")}
          />
        </div>
        <Kpi
          label={t("analytics.kpi.cost")}
          value={usd(agg.totalCostUsd)}
          note={t("analytics.kpi.cost.note")}
        />
      </section>

      {/* Per-surface cost split — the four metered surfaces (coding/pm/handoff/
          workflow) that partition the total above. Same hand-rolled split bar as
          the mode mix; no chart dependency. */}
      <section className="analytics-section analytics-cost-split">
        <h2 className="analytics-section-title">{t("analytics.cost.split.title")}</h2>
        <CostSplitBar segments={costSegments(agg.costBySurface, t, usd)} />
      </section>

      {/* Per-model spend — one row per model id, cost-descending. A row's
          source reads the ledger's own `costSource`: "priced" from raw tokens
          via the price table, "repriced" when a CLI figure was moved onto the
          rate Anthropic bills (its untouched figure is the badge's tooltip).
          Model ids are catalogue identifiers — still content-free. */}
      <section className="analytics-section">
        <h2 className="analytics-section-title">{t("analytics.models.title")}</h2>
        {modelRows.length ? (
          <table className="analytics-table">
            <thead>
              <tr>
                <th>{t("analytics.models.col.model")}</th>
                <th className="analytics-num">{t("analytics.models.col.cost")}</th>
                <th>{t("analytics.models.col.source")}</th>
                <th className="analytics-num">{t("analytics.models.col.input")}</th>
                <th className="analytics-num">{t("analytics.models.col.output")}</th>
                <th className="analytics-num">{t("analytics.models.col.cacheRead")}</th>
                <th className="analytics-num">{t("analytics.models.col.cacheWrite")}</th>
                <th className="analytics-num">{t("analytics.models.col.calls")}</th>
              </tr>
            </thead>
            <tbody>
              {modelRows.map((row) => (
                <tr key={row.modelId}>
                  <td className="analytics-mono">{row.modelId}</td>
                  <td className="analytics-num">{usd(row.costUsd)}</td>
                  <td>
                    <span
                      className={`analytics-source analytics-source-${row.costSource}`}
                      title={
                        row.reportedCostUsd !== null
                          ? usd(row.reportedCostUsd)
                          : undefined
                      }
                    >
                      {t(modelSourceLabelKey(row.costSource))}
                    </span>
                  </td>
                  <td className="analytics-num">{num(row.inputTokens)}</td>
                  <td className="analytics-num">{num(row.outputTokens)}</td>
                  <td className="analytics-num">{num(row.cacheReadTokens)}</td>
                  <td className="analytics-num">{num(row.cacheCreationTokens)}</td>
                  <td className="analytics-num">{num(row.callCount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="analytics-muted">{t("analytics.models.empty")}</p>
        )}
      </section>

      {/* Per-key spend — an assigned key by its id, Loom's own keys as one
          row. Key ids are ids, never the key. */}
      {keys ? (
        <section className="analytics-section">
          <h2 className="analytics-section-title">{t("analytics.keys.title")}</h2>
          {keys.length ? (
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>{t("analytics.keys.col.key")}</th>
                  <th className="analytics-num">{t("analytics.projects.col.cost")}</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((row) => (
                  <tr key={row.keyId ?? ""}>
                    {row.keyId !== null ? (
                      <td className="analytics-mono">{row.keyId}</td>
                    ) : (
                      <td>{t("analytics.keys.loom")}</td>
                    )}
                    <td className="analytics-num">{usd(row.costUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="analytics-muted">{t("analytics.empty")}</p>
          )}
        </section>
      ) : null}

      {/* As-billed reconciliation — only rendered once the billing-reconcile
          job has actually landed rows (admin key provisioned). Billed vs
          metered over the same billed days; the delta is the standing honesty
          metric for the metering pipeline. */}
      {billing && billedDays.length > 0 ? (
        <section className="analytics-section">
          <h2 className="analytics-section-title">{t("analytics.billing.title")}</h2>
          <div className="analytics-kpis">
            <Kpi
              label={t("analytics.billing.billed")}
              value={usd(billing.totalBilledUsd)}
            />
            <Kpi
              label={t("analytics.billing.metered")}
              value={usd(meteredInWindow.toFixed(6))}
            />
            <Kpi
              label={t("analytics.billing.delta")}
              value={
                billingDeltaPct === null
                  ? "—"
                  : `${billingDeltaPct >= 0 ? "+" : ""}${billingDeltaPct.toFixed(1)}%`
              }
            />
          </div>
          <p className="analytics-muted">
            {t("analytics.billing.note")}
            {nonTokenBilled > 0.000001
              ? ` ${t("analytics.billing.nonToken")} ${usd(nonTokenBilled.toFixed(6))}`
              : null}
          </p>
        </section>
      ) : null}

      {/* Daily usage timeseries. A day below the suppression threshold comes
          across with a null value; if EVERY day is suppressed there is nothing
          plottable, so fall back to the empty state rather than a blank axis. */}
      <section className="analytics-section">
        <h2 className="analytics-section-title">{t("analytics.timeseries.title")}</h2>
        {linePoints.some((p) => p.value != null) ? (
          <>
            <LineChart points={linePoints} ariaLabel={t("analytics.timeseries.sessions")} />
            <p className="analytics-axis-note">{t("analytics.timeseries.sessions")}</p>
          </>
        ) : (
          <p className="analytics-muted">
            {linePoints.length
              ? t("analytics.timeseries.allSuppressed")
              : t("analytics.timeseries.empty")}
          </p>
        )}
      </section>

      {/* Per-project breakdown. */}
      <section className="analytics-section">
        <h2 className="analytics-section-title">{t("analytics.projects.title")}</h2>
        {projects.length ? (
          <table className="analytics-table">
            <thead>
              <tr>
                <th>{t("analytics.projects.col.project")}</th>
                <th className="analytics-num">{t("analytics.projects.col.sessions")}</th>
                <th className="analytics-num">{t("analytics.projects.col.users")}</th>
                <th className="analytics-num">{t("analytics.projects.col.cost")}</th>
                {keys ? (
                  <th className="analytics-num">{t("analytics.projects.col.ownKey")}</th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {projects.map((row: AnalyticsProjectRow) => (
                <tr key={`${row.project.gitlabHost}:${row.project.gitlabProjectId}`}>
                  <td>{projectLabel(t, row.project)}</td>
                  <td className="analytics-num">
                    <Cell count={row.sessionCount} band={t("analytics.cell.suppressed")} />
                  </td>
                  <td className="analytics-num">
                    <Cell count={row.userCount} band={t("analytics.cell.suppressed")} />
                  </td>
                  <td className="analytics-num">{usd(row.totalCostUsd)}</td>
                  {keys ? (
                    <td className="analytics-num">
                      {ownKeyCost.has(row.project.gitlabProjectId) ? (
                        usd(ownKeyCost.get(row.project.gitlabProjectId)!)
                      ) : (
                        <span className="analytics-muted">—</span>
                      )}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="analytics-muted">{t("analytics.empty")}</p>
        )}
      </section>

      {/* Compact "recent sessions" widget — half-width glance at the latest
          sessions with a pipeline deep-link. Same audited-reveal contract as
          the full drill-down below (its own hook instance). */}
      <RecentSessionsWidget
        status={recent.status}
        rows={recent.data?.sessions ?? []}
        error={recent.error}
        projects={projects}
        onReveal={recent.reveal}
        onHide={recent.hide}
        usd={usd}
      />

      {/* Named drill-down — behind an explicit, audited reveal. */}
      <NamedPanel
        status={named.status}
        rows={named.data?.users ?? []}
        error={named.error}
        projects={projects}
        onReveal={named.reveal}
        onHide={named.hide}
        fmtDate={fmtDate}
        usd={usd}
      />

      {/* Session drill-down — the action ledger. Same audited-reveal shape. */}
      <SessionsPanel
        status={sessions.status}
        rows={sessions.data?.sessions ?? []}
        projection={sessions.data?.projection ?? null}
        error={sessions.error}
        projects={projects}
        timelines={sessions.timelines}
        onReveal={sessions.reveal}
        onHide={sessions.hide}
        onExpand={sessions.loadTimeline}
        fmtDate={fmtDate}
        usd={usd}
      />
    </>,
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const Kpi = ({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}): JSX.Element => (
  <div className="analytics-kpi">
    <span className="analytics-kpi-label">{label}</span>
    <span className="analytics-kpi-value">{value}</span>
    {note ? <span className="analytics-kpi-note">{note}</span> : null}
  </div>
);

const ORG_WIDE = "__org_wide__";

// How many of the most-recent sessions the compact widget shows. The server
// returns the full window ordered by lastActiveAt desc; we slice client-side.
const RECENT_LIMIT = 10;

/**
 * A person, named. The fallback to `User 450` is not decoration: display names
 * are resolved live on the viewer's own GitLab token and a miss is ordinary,
 * while the numeric id is what the fact table actually holds.
 */
const PersonCell = ({
  row,
}: {
  row: Parameters<typeof personLabel>[1];
}): JSX.Element => {
  const t = useT();
  const { name, handle } = personLabel(t, row);
  return (
    <span className="analytics-username">
      <span className="analytics-username-name">{name}</span>
      {handle ? <span className="analytics-username-handle">{handle}</span> : null}
    </span>
  );
};

/**
 * A session, named — "Refinement #84" over its id prefix, or just the prefix
 * on a row whose projection predates the `kind` column.
 *
 * The id never goes away. It is what every Cloud Logging query, every support
 * thread and the transcript URL are keyed by, so the name sits ABOVE it rather
 * than instead of it.
 */
const SessionCell = ({
  row,
}: {
  row: Parameters<typeof sessionLabel>[1];
}): JSX.Element => {
  const t = useT();
  const { name, ref } = sessionLabel(t, row);
  return (
    <span className="analytics-session-id">
      {name ? <span className="analytics-session-name">{name}</span> : null}
      <span className="analytics-mono">{ref}</span>
    </span>
  );
};

/**
 * The link to a session's chat, rendered ONLY where the server said there is
 * one to read and this viewer may read it (`hasTranscript`).
 *
 * Absent means no link and no explanation — the flag folds "no transcript
 * exists" together with "you are not a Tier-3 viewer", and a viewer without the
 * grant should not learn from this column that the surface exists.
 */
const TranscriptLink = ({
  row,
}: {
  row: AnalyticsNamedSessionRow;
}): JSX.Element | null => {
  const t = useT();
  if (row.hasTranscript !== true) return null;
  return (
    <Link
      className="analytics-transcript-link"
      to="/analytics/sessions/$sessionId"
      params={{ sessionId: row.sessionId }}
    >
      {t("analytics.transcript.link")}
    </Link>
  );
};

/** GitLab pipeline deep-link, rebuilt from the content-free wire fields (a
 *  numeric id + the project natural key). `null` when we can't form a path
 *  (no `projectPath` on the row) or the session never triggered a pipeline. */
const pipelineUrl = (
  project: AnalyticsNamedSessionRow["project"],
  pipelineId: number | null | undefined,
): string | null =>
  pipelineId != null && project.projectPath
    ? `https://${project.gitlabHost}/${project.projectPath}/-/pipelines/${pipelineId}`
    : null;

// Compact, half-width "recent sessions" glance: the latest sessions with the
// session id, user, project, total cost, and a deep-link to the session's most
// recent sandbox pipeline. Same audited-reveal contract as the full drill-down.
const RecentSessionsWidget = ({
  status,
  rows,
  error,
  projects,
  onReveal,
  onHide,
  usd,
}: {
  status: NamedStatus;
  rows: ReadonlyArray<AnalyticsNamedSessionRow>;
  error: string | null;
  projects: ReadonlyArray<AnalyticsProjectRow>;
  onReveal: (projectId?: number) => void;
  onHide: () => void;
  usd: (value: string) => string;
}): JSX.Element => {
  const t = useT();
  const [scope, setScope] = useState<string>(ORG_WIDE);
  const revealed = status !== "hidden";

  const doReveal = (): void =>
    onReveal(scope === ORG_WIDE ? undefined : Number(scope));

  return (
    <section className="analytics-section analytics-named analytics-half analytics-recent">
      <div className="analytics-named-head">
        <h2 className="analytics-section-title">{t("analytics.recent.title")}</h2>
        <div className="analytics-named-controls">
          <label className="analytics-scope">
            <span>{t("analytics.named.scope")}</span>
            <select
              value={scope}
              onChange={(e) => {
                setScope(e.target.value);
                if (revealed) onHide();
              }}
            >
              <option value={ORG_WIDE}>{t("analytics.named.scope.all")}</option>
              {projects.map((p) => (
                <option
                  key={`${p.project.gitlabHost}:${p.project.gitlabProjectId}`}
                  value={String(p.project.gitlabProjectId)}
                >
                  {projectLabel(t, p.project)}
                </option>
              ))}
            </select>
          </label>
          <button
            className="analytics-reveal"
            type="button"
            onClick={revealed ? onHide : doReveal}
          >
            {revealed ? t("analytics.recent.hide") : t("analytics.recent.reveal")}
          </button>
        </div>
      </div>
      <p className="analytics-named-caution">{t("analytics.named.caution")}</p>

      {status === "loading" ? (
        <p className="analytics-muted">{t("analytics.sessions.loading")}</p>
      ) : null}
      {status === "notEnabled" ? (
        <p className="analytics-muted">{t("analytics.named.notEnabled")}</p>
      ) : null}
      {status === "needsProject" ? (
        <p className="analytics-muted">{t("analytics.named.needsProject")}</p>
      ) : null}
      {status === "forbidden" ? (
        <p className="analytics-muted">{t("analytics.named.forbidden")}</p>
      ) : null}
      {status === "error" ? <p className="analytics-state-error">{error}</p> : null}

      {status === "ready" ? (
        rows.length ? (
          <table className="analytics-table">
            <thead>
              <tr>
                <th>{t("analytics.sessions.col.session")}</th>
                <th>{t("analytics.named.col.user")}</th>
                <th>{t("analytics.projects.col.project")}</th>
                <th className="analytics-num">{t("analytics.named.col.cost")}</th>
                <th>{t("analytics.recent.col.pipeline")}</th>
                <th>{t("analytics.transcript.col.chat")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, RECENT_LIMIT).map((row) => {
                const href = pipelineUrl(row.project, row.lastPipelineId);
                return (
                  <tr key={row.sessionId}>
                    <td>
                      <SessionCell row={row} />
                    </td>
                    <td>
                      <PersonCell row={row} />
                    </td>
                    <td>{projectLabel(t, row.project)}</td>
                    <td className="analytics-num">{usd(row.totalCostUsd)}</td>
                    <td className="analytics-mono">
                      {href ? (
                        <a href={href} target="_blank" rel="noreferrer">
                          #{row.lastPipelineId}
                        </a>
                      ) : (
                        t("analytics.recent.pipeline.none")
                      )}
                    </td>
                    <td>
                      <TranscriptLink row={row} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="analytics-muted">{t("analytics.sessions.empty")}</p>
        )
      ) : null}
    </section>
  );
};

const NamedPanel = ({
  status,
  rows,
  error,
  projects,
  onReveal,
  onHide,
  fmtDate,
  usd,
}: {
  status: NamedStatus;
  rows: ReadonlyArray<AnalyticsNamedUserRow>;
  error: string | null;
  projects: ReadonlyArray<AnalyticsProjectRow>;
  onReveal: (projectId?: number) => void;
  onHide: () => void;
  fmtDate: (when: string) => string;
  usd: (value: string) => string;
}): JSX.Element => {
  const t = useT();
  // Scope selection: org-wide (the whole gated population, org-wide viewers
  // only) or a single project (the Tier-2 path — the viewer must Maintain it).
  const [scope, setScope] = useState<string>(ORG_WIDE);
  const revealed = status !== "hidden";

  const doReveal = (): void =>
    onReveal(scope === ORG_WIDE ? undefined : Number(scope));

  return (
    <section className="analytics-section analytics-named">
      <div className="analytics-named-head">
        <h2 className="analytics-section-title">{t("analytics.named.title")}</h2>
        <div className="analytics-named-controls">
          <label className="analytics-scope">
            <span>{t("analytics.named.scope")}</span>
            <select
              value={scope}
              onChange={(e) => {
                setScope(e.target.value);
                if (revealed) onHide();
              }}
            >
              <option value={ORG_WIDE}>{t("analytics.named.scope.all")}</option>
              {projects.map((p) => (
                <option
                  key={`${p.project.gitlabHost}:${p.project.gitlabProjectId}`}
                  value={String(p.project.gitlabProjectId)}
                >
                  {projectLabel(t, p.project)}
                </option>
              ))}
            </select>
          </label>
          <button
            className="analytics-reveal"
            type="button"
            onClick={revealed ? onHide : doReveal}
          >
            {revealed ? t("analytics.named.hide") : t("analytics.named.reveal")}
          </button>
        </div>
      </div>
      <p className="analytics-named-caution">{t("analytics.named.caution")}</p>

      {status === "loading" ? <p className="analytics-muted">{t("analytics.named.loading")}</p> : null}
      {status === "notEnabled" ? (
        <p className="analytics-muted">{t("analytics.named.notEnabled")}</p>
      ) : null}
      {status === "needsProject" ? (
        <p className="analytics-muted">{t("analytics.named.needsProject")}</p>
      ) : null}
      {status === "forbidden" ? (
        <p className="analytics-muted">{t("analytics.named.forbidden")}</p>
      ) : null}
      {status === "error" ? (
        <p className="analytics-state-error">{error}</p>
      ) : null}

      {status === "ready" ? (
        rows.length ? (
          <table className="analytics-table">
            <thead>
              <tr>
                <th>{t("analytics.named.col.user")}</th>
                <th className="analytics-num">{t("analytics.named.col.sessions")}</th>
                <th className="analytics-num">{t("analytics.named.col.cost")}</th>
                <th className="analytics-num">{t("analytics.named.col.lastActive")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.user.gitlabHost}:${row.user.gitlabUserId}`}>
                  <td>
                    <PersonCell row={row} />
                  </td>
                  <td className="analytics-num">
                    {row.sessionCount.suppressed ? (
                      <span className="analytics-cell-suppressed">
                        {t("analytics.cell.suppressed")}
                      </span>
                    ) : (
                      row.sessionCount.value
                    )}
                  </td>
                  <td className="analytics-num">{usd(row.totalCostUsd)}</td>
                  <td className="analytics-num">{fmtDate(row.lastActiveAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="analytics-muted">{t("analytics.named.empty")}</p>
        )
      ) : null}
    </section>
  );
};

// Session drill-down — the per-session action ledger. Same audited-reveal
// contract as the named panel; each expanded timeline is its own audited read.
const SessionsPanel = ({
  status,
  rows,
  projection,
  error,
  projects,
  timelines,
  onReveal,
  onHide,
  onExpand,
  fmtDate,
  usd,
}: {
  status: NamedStatus;
  rows: ReadonlyArray<AnalyticsNamedSessionRow>;
  projection: AnalyticsProjection | null;
  error: string | null;
  projects: ReadonlyArray<AnalyticsProjectRow>;
  timelines: Readonly<Record<string, SessionTimelineState>>;
  onReveal: (projectId?: number) => void;
  onHide: () => void;
  onExpand: (sessionId: string) => void;
  fmtDate: (when: string) => string;
  usd: (value: string) => string;
}): JSX.Element => {
  const t = useT();
  const [scope, setScope] = useState<string>(ORG_WIDE);
  const [expanded, setExpanded] = useState<string | null>(null);
  const revealed = status !== "hidden";

  const doReveal = (): void => {
    setExpanded(null);
    onReveal(scope === ORG_WIDE ? undefined : Number(scope));
  };
  const toggle = (sessionId: string): void => {
    if (expanded === sessionId) {
      setExpanded(null);
      return;
    }
    setExpanded(sessionId);
    onExpand(sessionId);
  };

  return (
    <section className="analytics-section analytics-named analytics-sessions">
      <div className="analytics-named-head">
        <h2 className="analytics-section-title">{t("analytics.sessions.title")}</h2>
        <div className="analytics-named-controls">
          <label className="analytics-scope">
            <span>{t("analytics.named.scope")}</span>
            <select
              value={scope}
              onChange={(e) => {
                setScope(e.target.value);
                if (revealed) onHide();
              }}
            >
              <option value={ORG_WIDE}>{t("analytics.named.scope.all")}</option>
              {projects.map((p) => (
                <option
                  key={`${p.project.gitlabHost}:${p.project.gitlabProjectId}`}
                  value={String(p.project.gitlabProjectId)}
                >
                  {projectLabel(t, p.project)}
                </option>
              ))}
            </select>
          </label>
          <button
            className="analytics-reveal"
            type="button"
            onClick={revealed ? onHide : doReveal}
          >
            {revealed ? t("analytics.sessions.hide") : t("analytics.sessions.reveal")}
          </button>
        </div>
      </div>
      <p className="analytics-named-caution">{t("analytics.named.caution")}</p>
      {/* THE view an operator opens because they suspect a session is stuck —
          and every column in it (`lastActiveAt`, `actionCount`, cost) is
          projected, so a session working right now legitimately renders
          unchanged across refetches for a whole cron window. */}
      {status === "ready" ? <ProjectionNote info={projection} /> : null}

      {status === "loading" ? (
        <p className="analytics-muted">{t("analytics.sessions.loading")}</p>
      ) : null}
      {status === "notEnabled" ? (
        <p className="analytics-muted">{t("analytics.named.notEnabled")}</p>
      ) : null}
      {status === "needsProject" ? (
        <p className="analytics-muted">{t("analytics.named.needsProject")}</p>
      ) : null}
      {status === "forbidden" ? (
        <p className="analytics-muted">{t("analytics.named.forbidden")}</p>
      ) : null}
      {status === "error" ? <p className="analytics-state-error">{error}</p> : null}

      {status === "ready" ? (
        rows.length ? (
          <table className="analytics-table">
            <thead>
              <tr>
                <th>{t("analytics.sessions.col.session")}</th>
                <th>{t("analytics.named.col.user")}</th>
                <th>{t("analytics.projects.col.project")}</th>
                <th className="analytics-num">{t("analytics.named.col.lastActive")}</th>
                <th>{t("analytics.sessions.col.status")}</th>
                <th className="analytics-num">{t("analytics.sessions.col.actions")}</th>
                <th className="analytics-num">{t("analytics.named.col.cost")}</th>
                <th>{t("analytics.transcript.col.chat")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isOpen = expanded === row.sessionId;
                const timeline = timelines[row.sessionId];
                return [
                  <tr key={row.sessionId}>
                    <td>
                      <button
                        className="analytics-expand"
                        type="button"
                        aria-expanded={isOpen}
                        onClick={() => toggle(row.sessionId)}
                      >
                        {isOpen ? "▾" : "▸"} <SessionCell row={row} />
                      </button>
                    </td>
                    <td>
                      <PersonCell row={row} />
                    </td>
                    <td>{projectLabel(t, row.project)}</td>
                    <td className="analytics-num">{fmtDate(row.lastActiveAt)}</td>
                    <td>
                      {row.status}
                      {row.usedCoding ? ` · ${t("analytics.kpi.modeMix.coding")}` : ""}
                      {row.hasOpenMr ? " · MR" : ""}
                    </td>
                    <td className="analytics-num">{row.actionCount}</td>
                    <td className="analytics-num">{usd(row.totalCostUsd)}</td>
                    <td>
                      <TranscriptLink row={row} />
                    </td>
                  </tr>,
                  isOpen ? (
                    <tr key={`${row.sessionId}-timeline`} className="analytics-timeline-row">
                      <td colSpan={8}>
                        <SessionTimeline timeline={timeline} fmtDate={fmtDate} />
                      </td>
                    </tr>
                  ) : null,
                ];
              })}
            </tbody>
          </table>
        ) : (
          <p className="analytics-muted">{t("analytics.sessions.empty")}</p>
        )
      ) : null}
    </section>
  );
};

/** The outcome cell. `null` is a real state for several action kinds (a user
 *  message has no outcome), so it renders as an em dash rather than as text. */
const renderOutcome = (t: Translator, outcome: string | null): JSX.Element => {
  const label = outcomeLabel(t, outcome);
  if (label === null) return <span className="analytics-muted">—</span>;
  return (
    <span className={`analytics-outcome analytics-outcome-${label.tone}`} title={label.title}>
      {label.text}
    </span>
  );
};

const SessionTimeline = ({
  timeline,
  fmtDate,
}: {
  timeline: SessionTimelineState | undefined;
  fmtDate: (when: string) => string;
}): JSX.Element => {
  const t = useT();
  const time = useFormatDate({ timeStyle: "medium" });
  if (!timeline || timeline.status === "loading") {
    return <p className="analytics-muted">{t("analytics.sessions.timeline.loading")}</p>;
  }
  if (timeline.status === "error") {
    return <p className="analytics-state-error">{timeline.error}</p>;
  }
  if (!timeline.actions.length) {
    return <p className="analytics-muted">{t("analytics.sessions.timeline.empty")}</p>;
  }
  return (
    <table className="analytics-table analytics-timeline">
      <thead>
        <tr>
          <th>{t("analytics.sessions.timeline.col.time")}</th>
          <th>{t("analytics.sessions.timeline.col.kind")}</th>
          <th>{t("analytics.sessions.timeline.col.name")}</th>
          <th>{t("analytics.sessions.timeline.col.outcome")}</th>
          <th className="analytics-num">{t("analytics.sessions.timeline.col.duration")}</th>
        </tr>
      </thead>
      <tbody>
        {timeline.actions.map((a, i) => (
          <tr key={i}>
            <td className="analytics-num" title={fmtDate(a.occurredAt)}>
              {time(a.occurredAt)}
            </td>
            {/* Plain words in the cell, the exact stored token in the tooltip:
                the person reading this is usually holding a log query keyed on
                that token. */}
            <td title={a.kind}>{actionKindLabel(t, a.kind)}</td>
            <td className="analytics-mono">
              {a.name}
              {a.issueIid !== null ? ` #${a.issueIid}` : ""}
            </td>
            <td>{renderOutcome(t, a.outcome)}</td>
            <td className="analytics-num">
              {a.durationMs !== null ? `${(a.durationMs / 1000).toFixed(1)}s` : "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
